import { FC, memo } from 'react';
import { useDispatch } from 'react-redux';
import find from 'lodash/find';
import map from 'lodash/map';
import dayjs from 'dayjs';
import useForm from 'antd/lib/form/hooks/useForm';

import AccountDetails from 'components/Settings/AccountSupplierTab/AccountDetails';
import SupplierDescription from 'components/Settings/AccountSupplierTab/SupplierDescription';
import { PRODUCT_SOURCES_DATA } from 'components/Settings/dataConstants';
import WithSettingsForm from 'components/Settings/hocs/WithSettingsForm';
import SettingsSkeleton from 'components/Skeletons/Settings/SettingsSkeleton';
import { selectAccountSettings } from 'store/selectors/settingsSelectors';
import { userDataSelector } from 'store/selectors/userSelectors';
import { updateSettingsByKeyAction } from 'store/actions/settingsActions';
import { updateUserAction } from 'store/actions/userActions';
import { ESettingsKey } from 'store/reducers/settingsReducer/settingsTypes';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { updateUserRequest } from 'api/requests/messagesRequests';
import sendGTM from 'utils/sendGTM';
import { SettingsTabPaths } from 'components/Settings/constants';
import { momentFormats } from 'utils/constants/constants';
import { GTM_UPDATE_SETTINGS } from 'utils/constants/gtmConstants';
import { AccountTabStyled } from 'components/Settings/styled';
import { EAccountSupplierFields } from 'types/settings/enums';

const { FullName, Email, Website, EstablishmentDate, PhoneNumber, SkuAmount, ProductSources } = EAccountSupplierFields;

const AccountSupplierTab: FC = () => {
  const dispatch = useDispatch();
  const { phoneNumber, email, name, id } = useTypedSelector(userDataSelector);
  const { isLoading, ...accountSettings } = useTypedSelector(selectAccountSettings);
  const [form] = useForm();
  const initialValues = {
    phoneNumber,
    ...accountSettings,
    [FullName]: accountSettings[FullName] || name,
    [Email]: accountSettings[Email] || email,
    [Website]: accountSettings[Website] || '',
    [EstablishmentDate]: accountSettings[EstablishmentDate] && dayjs(accountSettings[EstablishmentDate]),
  } || {};

  const handleSubmit = async() => {
    const values = form.getFieldsValue();
    const payload = {
      ...values,
      [Email]: values.email?.toLowerCase(),
      [SkuAmount]: +values[SkuAmount],
      [EstablishmentDate]: values[EstablishmentDate]?.format(momentFormats.establishmentDate),
    };

    await dispatch(updateSettingsByKeyAction(payload, ESettingsKey.AccountSettings));

    if (form.isFieldTouched(PhoneNumber)) {
      await dispatch(updateUserAction({ [PhoneNumber]: form.getFieldValue(PhoneNumber) }));
      form.resetFields();
    }

    if (form.isFieldTouched(FullName)) {
      updateUserRequest(`${id}`, form.getFieldValue(FullName));
    }

    if (form.isFieldTouched(ProductSources) || form.isFieldTouched(SkuAmount)) {
      sendGTM({
        ...GTM_UPDATE_SETTINGS,
        ...(form.isFieldTouched(ProductSources)
          && {
            supplier_products_source: map(form.getFieldValue(ProductSources), (sourceId) =>
              find(PRODUCT_SOURCES_DATA, { id: sourceId })?.description.toLowerCase()),
          }),
        ...(form.isFieldTouched(SkuAmount) && { supplier_skus: form.getFieldValue(SkuAmount) }),
      });
    }
  };

  return (
    isLoading
      ? <SettingsSkeleton />
      : <AccountTabStyled>
        <WithSettingsForm
          form={form}
          initialValues={initialValues}
          rootPath={SettingsTabPaths.AccountSupplier}
          onSubmit={handleSubmit}
        >
          <AccountDetails />
          <SupplierDescription />
        </WithSettingsForm>
      </AccountTabStyled>
  );
};

export default memo(AccountSupplierTab);
